import React, {
  useCallback,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import styles from './exportedDocumentPdfListPage.module.css';
import usePageTitle from '../../hooks/title.hook';
import LoadingOverlay from '../../components/LoadingOverlay';
import { useDownloadFile } from '../../hooks/file.hook';
import { useDownloadDocumentBatchPdfFile, useGetSearchResultPdfFileDetail } from '../../hooks/api/document.hook';
import { CloseAlertModal, closeModalInitialState } from '../../components/AlertModal';
import Button from '../../components/Button/button';
import CustomFrame from '../../components/CustomFrame/customFrame';
import { ApiError } from '../../services/http';
import useEffectOnce from '../../hooks/useEffectOnce.hook';

function Component() {
  usePageTitle('PDFファイルダウンロード');

  // states
  const [closeAlertModal, setCloseAlertModal] = useState(closeModalInitialState);

  // hooks
  const downloadFile = useDownloadFile();
  const location = useLocation();

  // API's
  const getSearchResultPdfFileDetail = useGetSearchResultPdfFileDetail();
  const { request: downloadDocumentBatchPdfFile, loading: downloadDocumentBatchPdfFileLoading } = useDownloadDocumentBatchPdfFile();

  // constants
  const query = new URLSearchParams(location.search);
  const idFromQuery = query.get('exportId');

  const fetchSearchResultFileDetail = useCallback(async (searchKey: string) => {
    try {
      await getSearchResultPdfFileDetail.request(searchKey);
    } catch (e) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: (e as ApiError).message,
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
    }
  }, [getSearchResultPdfFileDetail, setCloseAlertModal]);

  // methods
  const onDownloadClick = useCallback(async (fileNumber: number, fileName: string) => {
    try {
      const form = { searchKey: idFromQuery as string, fileNumber, fileName };
      const searchResultFile = await downloadDocumentBatchPdfFile(form);
      downloadFile(searchResultFile);
    } catch (e) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: (e as ApiError).message,
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
    }
  }, [closeAlertModal, downloadFile, downloadDocumentBatchPdfFile]);

  const fileDownloadContent = getSearchResultPdfFileDetail.data?.pdfFileList.map(({ fileName, fileSize, fileNumber }) => (
    <div className={styles.fileDownloadContainer} key={fileNumber}>
      <div className={styles.fileDownloadRow}>
        <p>
          ファイル名：
          {fileName}
        </p>
        <Button size="smaller" content="ダウンロード" onClick={() => onDownloadClick(fileNumber, fileName)} />
      </div>
      <p>
        ファイルサイズ：
        {fileSize}
      </p>
    </div>
  ));

  // effects
  useEffectOnce(() => {
    if (!idFromQuery) {
      setCloseAlertModal({
        ...closeAlertModal,
        text: 'ダウンロードURLが無効です。',
        open: true,
        onCancel() {
          setCloseAlertModal({ ...closeAlertModal, open: false });
        },
      });
      return;
    }
    fetchSearchResultFileDetail(idFromQuery);
  });

  return (
    <>
      {
        getSearchResultPdfFileDetail.data && (
          <CustomFrame
            title={(
              <div className={styles.titleContainer}>
                <p>ダウンロードの準備が完了しました。</p>
                <p>下記のダウンロードボタンからファイルをダウンロードしてください。</p>
              </div>
            )}
            desc={(
              <div className={styles.descContainer}>
                <p>
                  処理番号：
                  {getSearchResultPdfFileDetail.data.searchResultNumber}
                </p>
                <p>
                  有効期限：
                  {getSearchResultPdfFileDetail.data.accessLimitDatetime}
                </p>
                <p>
                  リクエスト日時：
                  {getSearchResultPdfFileDetail.data.requestDatetime}
                </p>
                <div className={styles.fileDownloadSection}>
                  {fileDownloadContent}
                </div>
              </div>
            )}
            body={null}
          />
        )
      }
      {getSearchResultPdfFileDetail.loading && <LoadingOverlay />}
      {downloadDocumentBatchPdfFileLoading && <LoadingOverlay text={'ダウンロードが完了するまで、\nこのタブを閉じないでください。'} />}
      <CloseAlertModal open={closeAlertModal.open} text={closeAlertModal.text} onCancel={closeAlertModal.onCancel} closeButtonText="OK" />
    </>
  );
}

export default Component;
